import React, { useState, useEffect } from 'react';
import AppIcon from '../../../components/AppIcon';
import { getCompanyCurrency } from '../../../utils/currency';
import Button from '../../../components/ui/Button';

const TaxReconciliation = ({ compact = false }) => {
  const [companyCurrency, setCompanyCurrency] = useState(getCompanyCurrency());
  const [reconciliationData, setReconciliationData] = useState([
    {
      id: 1,
      period: 'Q4 2025',
      taxType: 'Sales Tax',
      calculated: 12450,
      paid: 12450,
      difference: 0,
      status: 'reconciled'
    },
    {
      id: 2,
      period: 'Q4 2025',
      taxType: 'Payroll Tax',
      calculated: 8320,
      paid: 8000,
      difference: 320,
      status: 'discrepancy'
    },
    {
      id: 3,
      period: 'Q4 2025',
      taxType: 'Income Tax',
      calculated: 24680,
      paid: 0,
      difference: 24680,
      status: 'unpaid'
    },
    {
      id: 4,
      period: 'Q3 2025',
      taxType: 'Sales Tax',
      calculated: 11200,
      paid: 11200,
      difference: 0,
      status: 'reconciled'
    }
  ]);

  // Load company currency on mount
  useEffect(() => {
    const currency = getCompanyCurrency();
    if (currency) setCompanyCurrency(currency);
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      reconciled: 'bg-green-100 text-green-800',
      discrepancy: 'bg-yellow-100 text-yellow-800',
      unpaid: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || colors?.reconciled;
  };

  const getStatusIcon = (status) => {
    const icons = {
      reconciled: 'CheckCircle',
      discrepancy: 'AlertTriangle',
      unpaid: 'XCircle'
    };
    return icons?.[status] || 'CheckCircle';
  };

  const formatCurrency = (amount) => {
    if (!companyCurrency) return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: companyCurrency
    })?.format(amount);
  };

  const displayItems = compact ? reconciliationData?.slice(0, 3) : reconciliationData;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <AppIcon name="CheckCircle" size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">Tax Reconciliation</h2>
        </div>
        {!compact && (
          <Button variant="outline" size="sm" iconName="Download">
            Export Report
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                Period
              </th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                Tax Type
              </th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                Calculated
              </th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                Paid
              </th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                Difference
              </th>
              <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {displayItems?.map((item) => (
              <tr key={item?.id} className="hover:bg-gray-50">
                <td className="py-3 text-sm text-gray-900">{item?.period}</td>
                <td className="py-3 text-sm text-gray-900">{item?.taxType}</td>
                <td className="py-3 text-sm text-gray-900 text-right">
                  {formatCurrency(item?.calculated)}
                </td>
                <td className="py-3 text-sm text-gray-900 text-right">
                  {formatCurrency(item?.paid)}
                </td>
                <td className={`py-3 text-sm font-semibold text-right ${
                  item?.difference === 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {item?.difference === 0 ? '-' : formatCurrency(item?.difference)}
                </td>
                <td className="py-3 text-center">
                  <span className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(item?.status)}`}>
                    <AppIcon name={getStatusIcon(item?.status)} size={12} />
                    {item?.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {compact && (
        <div className="mt-4 text-center">
          <Button variant="link" size="sm">
            View Full Reconciliation
          </Button>
        </div>
      )}
      {!compact && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AppIcon name="CheckCircle" size={18} className="text-green-600" />
              <span className="text-sm font-medium text-green-900">Reconciled</span>
            </div>
            <p className="text-2xl font-bold text-green-900">2</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AppIcon name="AlertTriangle" size={18} className="text-yellow-600" />
              <span className="text-sm font-medium text-yellow-900">Discrepancies</span>
            </div>
            <p className="text-2xl font-bold text-yellow-900">1</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AppIcon name="XCircle" size={18} className="text-red-600" />
              <span className="text-sm font-medium text-red-900">Unpaid</span>
            </div>
            <p className="text-2xl font-bold text-red-900">1</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxReconciliation;